import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { fetchIntegrations, getCachedIntegrations, type IntegrationListItem } from '../lib/catalog';
import { ArrowUp, ArrowRight, KeyRound, Check, Copy, Zap, Sparkles, Search, Globe, Plug, ShieldCheck, SquarePen, Folder, Clock, Library, Blocks, Box, Plus, ChevronRight, ChevronLeft, Landmark, PanelLeft, Users, LayoutGrid, UserRound, Send, Share2, Contact, Linkedin, MailCheck, Layers, Flame, Video, ShieldAlert, Eye, ScanLine, Cpu, Lightbulb, Workflow, CalendarClock, BadgeCheck, PenTool, Route, LineChart, FileSearch, FileText, Bot, PhoneCall, GraduationCap, Handshake, Gift, Filter, Signature, Network, MessageCircle, Radio, Inbox, PenSquare, Headset, Database, DatabaseZap, Target, RefreshCw, Magnet, LayoutTemplate, Megaphone, BarChart3, TrendingUp } from 'lucide-react';

// "Giga for ChatGPT" — ALTERNATE, MARKETING cut. Positions Giga as the
// marketing operating system layered onto ChatGPT. Leads with integration
// coverage: a segmented "how many connectors" comparison, then the full
// ontology of marketing tool categories Giga covers, then how easy adding one
// is. Built on the Giga design tokens (Tailwind: brand / ink / sand / cream).

const OPENAI_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg';
const GIGA_MARK = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png";
const GMAIL_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/gmail.svg';

// Segmented coverage meters (out of 10). ChatGPT alone is thin; Giga maxes it.
const METERS = [{
  metric: 'Available connectors',
  rows: [{
    who: 'ChatGPT',
    score: 3,
    brand: false
  }, {
    who: 'Giga',
    score: 10,
    brand: true
  }]
}];

// The ontology — the marketing tool categories Giga spans. Mirrors a
// "tools by category" wall; illustrative, expand from the real catalog.
const MARKETING_CATEGORIES = [{
  name: 'Email Finder',
  icon: Search
}, {
  name: 'Email Sender',
  icon: Send
}, {
  name: 'Multichannel Outreach',
  icon: Share2
}, {
  name: 'CRMs',
  icon: Contact
}, {
  name: 'LinkedIn Automation',
  icon: Linkedin
}, {
  name: 'Deliverability',
  icon: MailCheck
}, {
  name: 'Waterfall Enrichment',
  icon: Layers
}, {
  name: 'Email Warmer',
  icon: Flame
}, {
  name: 'Video Outreach',
  icon: Video
}, {
  name: 'Spam Testing',
  icon: ShieldAlert
}, {
  name: 'Visitor Identification',
  icon: Eye
}, {
  name: 'Scraping',
  icon: ScanLine
}, {
  name: 'Technology Lookup',
  icon: Cpu
}, {
  name: 'Prospect Insights',
  icon: Lightbulb
}, {
  name: 'Workflow Automation',
  icon: Workflow
}, {
  name: 'Social Scheduling',
  icon: CalendarClock
}, {
  name: 'Email Verification',
  icon: BadgeCheck
}, {
  name: 'AI Writing Assistants',
  icon: PenTool
}, {
  name: 'Attribution',
  icon: Route
}, {
  name: 'Sales Reporting',
  icon: LineChart
}, {
  name: 'Account Research',
  icon: FileSearch
}, {
  name: 'Proposals',
  icon: FileText
}, {
  name: 'AI Avatars',
  icon: Bot
}, {
  name: 'Phone Outreach',
  icon: PhoneCall
}, {
  name: 'Sales Coaching',
  icon: GraduationCap
}, {
  name: 'Warm Intros',
  icon: Handshake
}, {
  name: 'Prospect Gifting',
  icon: Gift
}, {
  name: 'Sales Funnel',
  icon: Filter
}, {
  name: 'E-Signature',
  icon: Signature
}, {
  name: 'Network Growth',
  icon: Network
}, {
  name: 'Buyer Engagement',
  icon: MessageCircle
}, {
  name: 'Social Listening',
  icon: Radio
}, {
  name: 'Inbox Management',
  icon: Inbox
}, {
  name: 'Copywriting',
  icon: PenSquare
}, {
  name: 'Customer Service',
  icon: Headset
}, {
  name: 'Prospecting Database',
  icon: Database
}, {
  name: 'Data Enrichment',
  icon: DatabaseZap
}, {
  name: 'Intent Signals',
  icon: Target
}, {
  name: 'CRM Syncing',
  icon: RefreshCw
}, {
  name: 'Lead Generation',
  icon: Magnet
}, {
  name: 'Landing Pages',
  icon: LayoutTemplate
}, {
  name: 'Ad Management',
  icon: Megaphone
}, {
  name: 'Analytics',
  icon: BarChart3
}, {
  name: 'SEO',
  icon: TrendingUp
}];

// ── "Built for teams" — the same sharing works for anything ────────
// The visual rotates a credential → file → skill; the tick marks move to
// whoever each is shared with. Spaces stay deliberately varied (a project, an
// area, an exploration).
const SHARE_TARGETS = [{
  key: 'Groups',
  icon: Users,
  people: false,
  items: ['Marketing team', 'Customer Support', 'Legal', 'Finance']
}, {
  key: 'Spaces',
  icon: LayoutGrid,
  people: false,
  items: ['Q3 Product Launch', 'Website Redesign', 'Early User Activation', 'Competitor Research', 'EMEA Expansion']
}, {
  key: 'Individuals',
  icon: UserRound,
  people: true,
  items: ['Priya Patel', 'Tom Nguyen', 'Sarah Chen', 'James Okafor']
}];

// Each rotates in at the top; `shares` lists which SHARE_TARGETS items it's
// shared with (ticked). `logo` is an image; otherwise `icon` is a lucide glyph.
const SHAREABLES = [{
  type: 'Credential',
  logo: GMAIL_ICON,
  name: 'Gmail inbox',
  sub: 'support@getgiga.com',
  shares: {
    Groups: ['Customer Support'],
    Spaces: ['Early User Activation'],
    Individuals: ['Tom Nguyen', 'Sarah Chen']
  }
}, {
  type: 'File',
  icon: FileText,
  name: '2025 Marketing Report',
  sub: 'PDF · 4.2 MB',
  shares: {
    Groups: ['Marketing team'],
    Spaces: ['Q3 Product Launch'],
    Individuals: ['Priya Patel']
  }
}, {
  type: 'Skill',
  icon: Sparkles,
  name: 'Competitor Analysis',
  sub: 'Custom skill',
  shares: {
    Groups: ['Marketing team', 'Finance'],
    Spaces: ['Competitor Research'],
    Individuals: ['James Okafor']
  }
}];

// ── Embedded expertise — Giga's structured answer vs ChatGPT's ─────
const UGC_SKILLS = [{
  initials: 'CH',
  name: 'Corey Haynes',
  skill: 'UGC growth loops',
  color: '#B0451E'
}, {
  initials: 'JP',
  name: 'Julia Pintar',
  skill: 'Creator sourcing & briefs',
  color: '#6D28D9'
}, {
  initials: 'DD',
  name: 'Dara Denney',
  skill: 'UGC ad creative',
  color: '#1E293B'
}];

// ── "Never hit a dead end" — Giga finds/builds a missing integration ──
const DEADEND_CHECKS = [{
  icon: Search,
  label: 'Checked the Giga directory',
  status: 'Not listed yet',
  tone: 'muted'
}, {
  icon: Globe,
  label: "Searched the web for Larkspur's API",
  status: 'Found',
  tone: 'green'
}, {
  icon: KeyRound,
  label: 'Detected auth — OAuth 2.0',
  status: 'Ready',
  tone: 'green'
}, {
  icon: Plug,
  label: 'Built the integration',
  status: 'Done',
  tone: 'green'
}];

// Which catalog tools ChatGPT can reach on its own (the handful that speak
// MCP / ship as first-party connectors). Illustrative — matched loosely by
// name/slug so the toggle has a believable "few lit, most dim" split.
const CHATGPT_MCP_KEYS = ['gmail', 'googledrive', 'google drive', 'googlecalendar', 'google calendar', 'github', 'slack', 'notion', 'dropbox', 'box', 'linear', 'hubspot', 'asana', 'atlassian', 'jira', 'confluence', 'canva', 'figma', 'stripe', 'intercom', 'sharepoint', 'onedrive', 'outlook', 'monday', 'zapier'];
function availableInChatGPT(item: IntegrationListItem): boolean {
  const hay = `${item.display_name} ${item.slug}`.toLowerCase();
  return CHATGPT_MCP_KEYS.some(k => hay.includes(k));
}

// The catalog API has NO popularity field and returns tools alphabetically (so
// the raw top is obscure "0codekit"-type apps). This is a curated, marketing-
// first ranking of real catalog slugs; matches float to the top in this order,
// everything else keeps the API's alphabetical order after them.
const POPULAR_SLUGS = ['hubspot', 'salesforce-rest-api', 'mailchimp', 'klaviyo-oauth', 'google-ads', 'meta-ads', 'facebook', 'linkedin', 'tiktok-com', 'stripe', 'slack', 'notion', 'intercom', 'zendesk-com', 'pipedrive-com', 'apollo', 'salesloft', 'marketo', 'attio', 'typeform-com-openapi', 'webflow', 'wordpress', 'canva', 'segment-com', 'analytics-googleapis-com', 'amplitude', 'posthog', 'sendgrid', 'github', 'linear', 'asana', 'jira', 'monday', 'clickup', 'todoist-com', 'airtable', 'zapier', 'calendly', 'zoom', 'microsoft-teams', 'google-drive', 'google-calendar', 'sheets-googleapis-com', 'googleapis-com-gmail-gmail', 'dropbox', 'sharepoint', 'confluence', 'sentry', 'discord-com', 'xero', 'freshdesk-com', 'snowflake-com-openapi'];
const POPULAR_RANK = new Map(POPULAR_SLUGS.map((s, i) => [s, i]));

// A 10-segment meter bar that fills, segment-by-segment, when it scrolls into
// view. The filled segments stagger in left→right so the bar reads as "filling
// up"; `delay` lets the Giga row start after ChatGPT's for a payoff beat.
const SEGMENT = {
  hidden: {
    opacity: 0,
    scaleX: 0.25
  },
  visible: {
    opacity: 1,
    scaleX: 1,
    transition: {
      duration: 0.34,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function Meter({
  score,
  brand,
  delay = 0
}: {
  score: number;
  brand: boolean;
  delay?: number;
}) {
  return <motion.div className="flex gap-1" initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-12% 0px -12% 0px'
  }} transition={{
    staggerChildren: 0.07,
    delayChildren: delay
  }} data-id="element-8956" data-mp="36tedus34rva3">
      {Array.from({
      length: 10
    }).map((_, i) => i < score ? <motion.div key={i} variants={SEGMENT} className={['h-2.5 flex-1 rounded-[3px] origin-left', brand ? 'bg-brand' : 'bg-ink/60'].join(' ')} data-id="element-8957" data-mp="3kjq7wwja043v" /> : <div key={i} className="h-2.5 flex-1 rounded-[3px] bg-sand-100" data-id="element-8958" data-mp="2nv21yyku47at" />)}
    </motion.div>;
}

// Styled tooltip (no library). Renders into document.body via a portal so it's
// never clipped by the grid's scroll container, and is positioned top-center
// above the trigger using its live viewport rect. Nothing renders until hover,
// so it's SSR/prerender-safe.
function Tip({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({
      x: r.left + r.width / 2,
      y: r.top
    });
  };
  const hide = () => setPos(null);
  return <span ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative inline-flex" data-id="element-8959" data-mp="2x3ywau0jz7in">
      {children}
      {pos && typeof document !== 'undefined' && createPortal(<div style={{
      position: 'fixed',
      left: pos.x,
      top: pos.y,
      transform: 'translate(-50%, calc(-100% - 9px))'
    }} className="pointer-events-none z-[100] whitespace-nowrap rounded-md bg-ink text-white text-[11px] font-medium leading-none px-2 py-1.5 shadow-lg" data-id="element-8960" data-mp="24fjkegcpx5bj">
          {label}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-ink" data-id="element-8961" data-mp="2if2sxa9fyiod" />
        </div>, document.body)}
    </span>;
}

// A real integration from the Giga catalog (logo + name + first category).
// Two support marks sit in the top-right: Giga (always lit — everything works
// in Giga) and ChatGPT (faded when it can't reach the tool). Falls back to an
// initial tile when a logo is missing or fails to load.
function IntegrationTile({
  item,
  available
}: {
  item: IntegrationListItem;
  available: boolean;
}) {
  const [broken, setBroken] = useState(false);
  return <div className="relative flex items-center gap-3 p-3 rounded-xl bg-cream border border-sand-200 min-w-0" data-id="element-8962" data-mp="3q7t8ud35cdgn">
      {item.logo_url && !broken ? <img src={item.logo_url} alt="" loading="lazy" onError={() => setBroken(true)} className="w-8 h-8 object-contain rounded flex-none" data-id="element-8963" data-mp="3l987gac5rnqm" /> : <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center text-sm font-bold flex-none" data-id="element-8964" data-mp="3b9r5a2w47rze">{item.display_name.charAt(0).toUpperCase()}</div>}
      <div className="min-w-0 flex flex-col pr-10" data-id="element-8965" data-mp="37a8g2ux9guqr">
        <span className="text-[13px] font-semibold text-ink truncate" data-id="element-8966" data-mp="19hcmz2pnslig">{item.display_name}</span>
        {item.categories[0] && <span className="text-[10px] text-ink-faint uppercase tracking-wider truncate" data-id="element-8967" data-mp="15halounu5u3p">{item.categories[0].name}</span>}
      </div>
      {/* Support marks — Giga always, ChatGPT faded when unsupported */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5" data-id="element-8968" data-mp="33rszo9h2111g">
        <Tip label="Works in Giga" data-id="element-8969" data-mp="2pzusw5atfh8g">
          <img src={GIGA_MARK} alt="" className="w-3.5 h-3.5 rounded-[2px]" data-id="element-8970" data-mp="2pwqj8sekztae" />
        </Tip>
        <Tip label={available ? 'Works in ChatGPT' : "Doesn't work in ChatGPT"} data-id="element-8971" data-mp="2u0ogsyo9cw1c">
          <img src={OPENAI_ICON} alt="" className={['w-3.5 h-3.5 transition-opacity', available ? '' : 'opacity-20 grayscale'].join(' ')} data-id="element-8972" data-mp="3hrapms636kce" />
        </Tip>
      </div>
    </div>;
}
function MeterRow({
  who,
  score,
  brand
}: {
  who: string;
  score: number;
  brand: boolean;
}) {
  const logo = who === 'Giga' ? GIGA_MARK : OPENAI_ICON;
  return <div className="grid grid-cols-[92px_1fr] md:grid-cols-[120px_1fr] items-center gap-3 md:gap-4" data-id="element-8973" data-mp="1xa5rvry3vh6u">
      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-ink" data-id="element-8974" data-mp="2298xbdhzzpfc">
        <img src={logo} alt="" className={['w-4 h-4', who === 'Giga' ? 'rounded-[3px]' : ''].join(' ')} data-id="element-8975" data-mp="226w8cgdtoess" />
        {who}
      </span>
      <Meter score={score} brand={brand} delay={brand ? 0.35 : 0.1} data-id="element-8976" data-mp="1pr6atsy4a1qn" />
    </div>;
}

// Rotating "share anything" visual — a credential, then a file, then a skill,
// animating in/out at the top while the tick marks below move to whoever each
// item is shared with.
export function TeamSharingVisual() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive(i => (i + 1) % SHAREABLES.length), 3200);
    return () => clearInterval(id);
  }, []);
  const item = SHAREABLES[active];
  const shared = (key: string, label: string) => (item.shares as Record<string, string[]>)[key]?.includes(label);
  return <div className="flex flex-col items-center" data-id="element-8977" data-mp="1fzikxwh7ksra">
      {/* The rotating shareable */}
      <div className="flex items-center justify-center w-full h-[76px]" data-id="element-8978" data-mp="s9od5tbqxudw">
        <AnimatePresence mode="wait" data-id="element-8979" data-mp="nb4q1plf2cjy">
          <motion.div key={item.type} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="inline-flex items-center gap-3 bg-white border border-sand-200 rounded-2xl shadow-[0_6px_24px_rgba(43,33,26,0.08)] pl-3 pr-3.5 py-2.5" data-id="element-8980" data-mp="na20bkztyg5m">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-sand-50 border border-sand-200 flex-none" data-id="element-8981" data-mp="9bsg1flk4i75">
              {item.logo ? <img src={item.logo} alt="" className="w-6 h-6" data-id="element-8982" data-mp="ecge2l87dzav" /> : <item.icon className="w-5 h-5 text-brand-dark" data-id="element-8983" data-mp="j96rt1wjcit4" />}
            </span>
            <div className="flex flex-col text-left pr-1" data-id="element-8984" data-mp="nas0eb21eaxi">
              <span className="font-title font-bold text-[15px] leading-tight" data-id="element-8985" data-mp="na8sln63p2x7">{item.name}</span>
              <span className="font-mono text-[11.5px] text-ink-muted" data-id="element-8986" data-mp="2l2mi1xopnvnk">{item.sub}</span>
            </div>
            <span className="inline-flex items-center bg-cream border border-sand-200 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-muted flex-none" data-id="element-8987" data-mp="2p4hjd26n691c">{item.type}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Connector */}
      <div className="w-0 h-8 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-8988" data-mp="27tcc9yzyom8l" />

      {/* Share-with targets */}
      <div className="relative w-full border-2 border-dashed border-[#D6CCC0] rounded-[24px] bg-sand-50 pt-9 px-4 md:px-6 pb-5 md:pb-6" data-id="element-8989" data-mp="23pecdm2tb7eh">
        <div className="absolute -top-3 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-8990" data-mp="2vi5bq5fslc7y">
          <Share2 className="w-3.5 h-3.5 text-brand-lighter" data-id="element-8991" data-mp="2rfqqxfskc2e1" />
          <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-8992" data-mp="2mi8peagvkpg2">Share with</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-8993" data-mp="1g2y2adv2ju27">
          {SHARE_TARGETS.map(({
          key,
          icon: Icon,
          items,
          people
        }) => <div key={key} className="bg-white border border-sand-200 rounded-2xl p-4 md:p-5 flex flex-col gap-3" data-id="element-8994" data-mp="1cjh6xcuq6tpw">
              <div className="flex items-center gap-2" data-id="element-8995" data-mp="1hi0shc5eq3hq">
                <Icon className="w-4 h-4 text-brand-dark flex-none" data-id="element-8996" data-mp="1hgpmr6seilnz" />
                <span className="font-title font-bold text-[15px]" data-id="element-8997" data-mp="13j1485lt3qm3">{key}</span>
              </div>
              <div className="flex flex-col gap-1.5 items-start" data-id="element-8998" data-mp="17jbbrmp8yp32">
                {items.map(label => {
              const on = shared(key, label);
              return <span key={label} className={['inline-flex items-center gap-2 text-[12px] font-medium rounded-full pl-2.5 pr-2 py-1 border transition-colors duration-300', on ? 'bg-brand/[0.07] border-brand/40 text-ink' : 'bg-cream border-sand-200 text-ink'].join(' ')} data-id="element-8999" data-mp="ykpvjy78zm5g">
                      {people && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand/10 text-brand-dark text-[8px] font-bold flex-none" data-id="element-9000" data-mp="auvj3hqubzlq">{label.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>}
                      <span data-id="element-9001" data-mp="3scj07mztg4de">{label}</span>
                      <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-none" data-id="element-9002" data-mp="3sdkjgeqzjx67">
                        <AnimatePresence data-id="element-9003" data-mp="3ngc3mktb8584">
                          {on && <motion.span key="t" initial={{
                      opacity: 0,
                      scale: 0.3
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      scale: 0.3
                    }} transition={{
                      duration: 0.2
                    }} className="inline-flex" data-id="element-9004" data-mp="58uysdxpxstt">
                            <Check className="w-3.5 h-3.5 text-brand-dark" strokeWidth={2.5} data-id="element-9005" data-mp="oyolwephnjrp" />
                          </motion.span>}
                        </AnimatePresence>
                      </span>
                    </span>;
            })}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}

// A replica of the ChatGPT composer box — reused for the prompt (with a send
// button) and for the answer (without).
function ChatBox({
  send = false,
  children
}: {
  send?: boolean;
  children: React.ReactNode;
}) {
  return <div className="w-full bg-white border border-sand-200 rounded-[26px] shadow-[0_10px_44px_rgba(43,33,26,0.09)] py-4 pl-5 pr-3 flex items-center gap-3" data-id="element-9006" data-mp="29at5907xqsck">
      <div className="flex-1 text-[15px] md:text-[16px] leading-snug text-ink" data-id="element-9007" data-mp="2e8bekrpv79l3">{children}</div>
      {send && <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9008" data-mp="1hi26oj2s4rk5"><ArrowUp className="w-4 h-4" data-id="element-9009" data-mp="1qktiwl3hbrcu" /></span>}
    </div>;
}

// A small dashed vertical connector between the stacked flow rows.
function FlowLink() {
  return <span className="self-center w-0 h-11 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9010" data-mp="1vcbip5trnlw8" />;
}

// ChatGPT-style "tool calling" indicator — cycles through Giga's tool-call
// statuses with the Giga mark and bouncing dots, the way a chat shows work.
const GIGA_STATUSES = ['Searching the Giga directory…', 'Calling Giga tools…', 'Pulling UGC integrations…', 'Finding operator skills…'];
function GigaWorking({
  statuses = GIGA_STATUSES,
  bare = false
}: {
  statuses?: string[];
  bare?: boolean;
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % statuses.length), 1600);
    return () => clearInterval(id);
  }, [statuses]);
  const label = <AnimatePresence mode="wait" data-id="element-9011" data-mp="cdneofgix7f8">
      <motion.span key={i} initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4
    }} transition={{
      duration: 0.2
    }} className={bare ? 'text-[14.5px] text-ink-faint whitespace-nowrap' : 'font-mono text-[11.5px] text-ink-muted whitespace-nowrap'} data-id="element-9012" data-mp="ceyiugdvsh6v">{statuses[i % statuses.length]}</motion.span>
    </AnimatePresence>;
  if (bare) return <div className="inline-flex items-center gap-2.5" data-id="element-9013" data-mp="3ksoxndxp6b3d">
      <img src={GIGA_MARK} alt="" className="w-5 h-5 rounded-[5px] animate-pulse" data-id="element-9014" data-mp="3kt74t5lwe9q2" />{label}
    </div>;
  return <div className="inline-flex self-start items-center gap-2 bg-cream border border-sand-200 rounded-full pl-2 pr-3 py-1.5" data-id="element-9015" data-mp="29opa53kfdjq0">
      <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px] animate-pulse" data-id="element-9016" data-mp="25lsxrulhajui" />{label}
      <span className="flex items-center gap-0.5" data-id="element-9017" data-mp="20qn8clsvan1o">
        {[0, 150, 300].map(d => <span key={d} className="w-1 h-1 rounded-full bg-ink-faint animate-bounce" style={{
        animationDelay: `${d}ms`
      }} data-id="element-9018" data-mp="1voxw52tws3c0" />)}
      </span>
    </div>;
}

// ── Getting started — a scroll-driven recreation of the ChatGPT plugin
// install flow. Three screens rebuilt in markup (chats/recents removed), the
// left frame swapping to the active step as the steps scroll past centre.
const CAPTIONS = ['Search the ChatGPT plugin store for Giga', 'Click Install'];
const FEATURED = [{
  name: 'Data Analytics',
  desc: 'Answer product and business questions',
  icon: BarChart3,
  color: '#7C5CFC'
}, {
  name: 'GitHub',
  desc: 'Triage PRs, issues, CI, and flows',
  logo: 'github'
}, {
  name: 'Investment Banking',
  desc: 'M&A, capital markets, valuation',
  icon: Landmark,
  color: '#3A6B4E'
}, {
  name: 'Google Drive',
  desc: 'Work across Drive, Docs, Sheets',
  logo: 'googledrive'
}];
function ChatGPTSidebar() {
  const items: [string, any, boolean][] = [['New chat', SquarePen, false], ['Projects', Folder, false], ['Scheduled', Clock, false], ['Library', Library, false], ['Plugins', Blocks, true], ['Agents', Bot, false], ['GPTs', Box, false]];
  return <div className="hidden sm:flex w-[150px] md:w-[176px] flex-none flex-col gap-0.5 bg-[#F9F9F9] border-r border-black/[0.06] py-3 px-2" data-id="element-9019" data-mp="f4npo15pz4zu">
      <div className="flex items-center justify-between px-2 pb-3" data-id="element-9020" data-mp="k38p3yd5dkn8">
        <span className="text-[13px] font-semibold text-[#0D0D0D]" data-id="element-9021" data-mp="owbia5e0is2w">ChatGPT</span>
        <span className="flex items-center gap-1.5 text-[#9A9A9A]" data-id="element-9022" data-mp="3nls4w1en2y15"><Search className="w-3.5 h-3.5" data-id="element-9023" data-mp="3nj5xq2vnukjg" /><PanelLeft className="w-3.5 h-3.5" data-id="element-9024" data-mp="3sjtp4pnsx3e7" /></span>
      </div>
      {items.map(([label, Icon, active]) => <span key={label} className={['flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px]', active ? 'bg-black/[0.06] font-medium text-[#0D0D0D]' : 'text-[#3D3D3D]'].join(' ')} data-id="element-9025" data-mp="3kj5jmg81zlex">
          <Icon className="w-4 h-4 text-[#5C5C5C]" data-id="element-9026" data-mp="3u978ha88d7uw" />{label}
        </span>)}
    </div>;
}
function ScreenBrowse({
  typed
}: {
  typed: string;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3.5" data-id="element-9027" data-mp="3pavz10lukzd7">
      <div className="flex justify-center" data-id="element-9028" data-mp="31lkwpeoumwwy">
        <div className="inline-flex items-center gap-0.5 bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-9029" data-mp="2wlq496pfdz9s">
          <span className="px-3 py-1 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-9030" data-mp="3bipk1hufkyfc">Plugins</span>
          <span className="px-3 py-1 text-[#6A6A6A]" data-id="element-9031" data-mp="36jcz52n4e1fe">Skills</span>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3" data-id="element-9032" data-mp="qsi0rkwwvs3n">
        <div className="flex flex-col" data-id="element-9033" data-mp="qqx9c45w8pa0">
          <span className="text-[20px] font-bold text-[#0D0D0D] leading-tight" data-id="element-9034" data-mp="lu6rbbaj3jly">Plugins</span>
          <span className="text-[12px] text-[#8A8A8A]" data-id="element-9035" data-mp="tdfzj17atc9f">Work with ChatGPT across your favorite tools.</span>
        </div>
        <span data-cursor="search" className={['inline-flex items-center gap-1.5 rounded-full border w-[140px] overflow-hidden whitespace-nowrap px-2.5 py-1.5 text-[11px] flex-none', typed ? 'border-black/25 text-[#0D0D0D]' : 'border-black/10 text-[#9A9A9A]'].join(' ')} data-id="element-9036" data-mp="ycj6ir6u9sk1"><Search className="w-3 h-3 text-[#9A9A9A] flex-none" data-id="element-9037" data-mp="ofwkw06ouxgy" />{typed ? <span data-id="element-9038" data-mp="1c47jewowwl22">{typed}<span className="inline-block w-px h-[10px] bg-[#0D0D0D] align-middle ml-px animate-pulse" data-id="element-9039" data-mp="1c4fucj1h5gam" /></span> : 'Search plugins'}</span>
      </div>
      <div className="flex flex-col gap-1.5" data-id="element-9040" data-mp="13bvsl9pvyvlk">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-9041" data-mp="13b3wlxsdmvur">Installed <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-9042" data-mp="17b4nwj0kr1j8" /></span>
        <div className="flex gap-1.5" data-id="element-9043" data-mp="1v1h3mg22a2kd">
          <img src={GIGA_MARK} alt="" className="w-8 h-8 rounded-lg flex-none" data-id="element-9044" data-mp="1v0fe56katdj4" />
          {['#4285F4', '#E8710A', '#EA4335', '#F9AB00', '#0F9D58'].map((c, i) => <span key={i} className="w-8 h-8 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-9045" data-mp="c56z9jr5r4nl" data-mpi={i}><span className="w-3.5 h-3.5 rounded-[3px]" style={{
            background: c
          }} data-id="element-9046" data-mp="752j5uu5wbxw" /></span>)}
        </div>
      </div>
      <span className="inline-flex items-center gap-0.5 self-start bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-9047" data-mp="28kgd0ie6no7">
        <span className="px-2.5 py-0.5 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-9048" data-mp="uv58i36oz3st">Public</span>
        <span className="px-2.5 py-0.5 text-[#6A6A6A]" data-id="element-9049" data-mp="pvtpeniurm82">Personal</span>
      </span>
      <div className="flex flex-col gap-2" data-id="element-9050" data-mp="l5lk6i07mhqk">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-9051" data-mp="l4aj4py5fo35">Featured <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-9052" data-mp="h38flwx78pvd" /></span>
        <div className="grid grid-cols-2 gap-x-5 gap-y-3" data-id="element-9053" data-mp="1dmfwocl192h0">
          {FEATURED.map(f => <div key={f.name} className="flex items-center gap-2 min-w-0" data-id="element-9054" data-mp="18v7nnhnm7j4d">
              {f.logo ? <span className="w-7 h-7 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-9055" data-mp="18tczzvkylcwl"><img src={`https://cdn.simpleicons.org/${f.logo}`} alt="" className="w-4 h-4" data-id="element-9056" data-mp="2wh0wgpq58buv" /></span> : <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-none" style={{
            background: f.color
          }} data-id="element-9057" data-mp="2wivftzlrnvwu"><f.icon className="w-3.5 h-3.5 text-white" data-id="element-9058" data-mp="35i8jgbn7lq9n" /></span>}
              <div className="flex flex-col min-w-0" data-id="element-9059" data-mp="35fckcjzmkvjm">
                <span className="text-[12px] font-medium text-[#0D0D0D] leading-tight" data-id="element-9060" data-mp="3ag0irqq6oenv">{f.name}</span>
                <span className="text-[11px] text-[#9A9A9A] truncate" data-id="element-9061" data-mp="3feljrsd9f2dd">{f.desc}</span>
              </div>
              <Plus className="w-3.5 h-3.5 text-[#B0B0B0] ml-auto flex-none" data-id="element-9062" data-mp="3k74y4rjru1j8" />
            </div>)}
        </div>
      </div>
    </div>;
}
function ScreenDetail({
  installHover
}: {
  installHover: boolean;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3" data-id="element-9063" data-mp="3t94h3pecjfot">
      <span className="inline-flex items-center gap-1 text-[12px] text-[#6A6A6A]" data-id="element-9064" data-mp="21ruuvylwc21"><ChevronLeft className="w-3.5 h-3.5" data-id="element-9065" data-mp="1y3d8qsrg2vdz" />Plugins</span>
      <img src={GIGA_MARK} alt="" className="w-12 h-12 rounded-xl" data-id="element-9066" data-mp="1y1saot5x8ubd" />
      <div className="flex items-center justify-between gap-3" data-id="element-9067" data-mp="1t6mtu8rdm5u5">
        <span className="text-[22px] font-bold text-[#0D0D0D]" data-id="element-9068" data-mp="27u7u7uwv9qyd">Giga</span>
        <span className="flex items-center gap-2 flex-none" data-id="element-9069" data-mp="2337ygszhhexg">
          <span className="text-[#9A9A9A] text-[13px]" data-id="element-9070" data-mp="231n00sa1tbub">•••</span>
          <span data-cursor="install" className={['inline-flex items-center bg-[#0D0D0D] text-white rounded-full px-4 py-1.5 text-[12px] font-medium origin-center transition-transform duration-200', installHover ? 'scale-[1.2] shadow-lg' : ''].join(' ')} data-id="element-9071" data-mp="2gtuvmgpliwrf">Install</span>
        </span>
      </div>
      <span className="text-[12px] text-[#8A8A8A] -mt-1.5" data-id="element-9072" data-mp="2ltfw4vkezd69">Company Operating System</span>
      <div className="flex flex-col gap-1.5" data-id="element-9073" data-mp="we9ej876o6o6">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D]" data-id="element-9074" data-mp="wfazq5hwy9jm">App</span>
        <div className="flex items-center gap-2" data-id="element-9075" data-mp="rgpz44yr1nun"><img src={GIGA_MARK} alt="" className="w-6 h-6 rounded-md" data-id="element-9076" data-mp="ri158bfo37ox" /><span className="text-[12px] text-[#0D0D0D]" data-id="element-9077" data-mp="334cqq7yvvwoc">Giga</span></div>
      </div>
      <div className="flex flex-col" data-id="element-9078" data-mp="382e90qohjryr">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D] mb-1" data-id="element-9079" data-mp="381cx6jznb1fz">Information</span>
        {([['Developer', 'App developer'], ['Category', 'Other'], ['Version', '1.0.0']] as [string, string][]).map(([k, v]) => <div key={k} className="flex justify-between border-b border-black/[0.06] py-1.5 text-[11.5px]" data-id="element-9080" data-mp="2ya8h1gy8hajk"><span className="text-[#8A8A8A]" data-id="element-9081" data-mp="2kemy9rilfmzl">{k}</span><span className="text-[#0D0D0D]" data-id="element-9082" data-mp="2pc6cirfajzrj">{v}</span></div>)}
      </div>
    </div>;
}
function ChatGPTFrame({
  step,
  typed,
  installHover
}: {
  step: number;
  typed: string;
  installHover: boolean;
}) {
  return <div className="rounded-2xl border border-sand-200 overflow-hidden bg-white shadow-[0_10px_40px_rgba(43,33,26,0.08)] flex h-[410px] md:h-[470px] text-left" data-id="element-9083" data-mp="2fcnvaiqrms36">
      <ChatGPTSidebar data-id="element-9084" data-mp="8l6g4lehglpc" />
      <div className="flex-1 relative bg-white overflow-hidden" data-id="element-9085" data-mp="dmm3wl81p6bf">
        <AnimatePresence mode="wait" data-id="element-9086" data-mp="djq7j1buipp3">
          <motion.div key={step} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="absolute inset-0 overflow-hidden" data-id="element-9087" data-mp="2xieiqcys6l9l">
            {step === 0 && <ScreenBrowse typed={typed} data-id="element-9088" data-mp="2it8mmxv8pb5x" />}
            {step === 1 && <ScreenDetail installHover={installHover} data-id="element-9089" data-mp="2ispe27rhbz4k" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
function GetStartedFlow() {
  const [screen, setScreen] = useState(0);
  const [typed, setTyped] = useState('');
  const [installHover, setInstallHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [pointer, setPointer] = useState({
    x: 50,
    y: 58
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef('center');

  // Point the cursor at a real element's centre, expressed as a % of the frame
  // container — so it tracks the layout at any container width (and on resize).
  const place = (sel: string) => {
    targetRef.current = sel;
    if (sel === 'center') {
      setPointer({
        x: 50,
        y: 58
      });
      return;
    }
    const c = containerRef.current;
    const el = c?.querySelector<HTMLElement>(`[data-cursor="${sel}"]`);
    if (!c || !el) return;
    const er = el.getBoundingClientRect();
    const cr = c.getBoundingClientRect();
    setPointer({
      x: (er.left + er.width / 2 - cr.left) / cr.width * 100,
      y: (er.top + er.height / 2 - cr.top) / cr.height * 100
    });
  };

  // A looping, choreographed "screen recording": cursor → search box, type Giga,
  // results appear, cursor → Install (which grows on hover).
  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    let stopped = false;
    const at = (ms: number, fn: () => void) => {
      timers.add(setTimeout(fn, ms));
    };
    const cycle = () => {
      if (stopped) return;
      timers.clear();
      setScreen(0);
      setTyped('');
      setInstallHover(false);
      setPressed(false);
      place('center');
      at(500, () => place('search'));
      at(1550, () => setPressed(true));
      at(1720, () => setPressed(false));
      at(1760, () => setTyped('G'));
      at(1890, () => setTyped('Gi'));
      at(2020, () => setTyped('Gig'));
      at(2150, () => setTyped('Giga'));
      at(2800, () => setScreen(1)); // → Giga detail
      at(3200, () => place('install'));
      at(3650, () => setInstallHover(true)); // enlarge on hover
      at(4050, () => setPressed(true));
      at(4250, () => setPressed(false));
      at(5400, cycle);
    };
    cycle();
    const onResize = () => place(targetRef.current);
    window.addEventListener('resize', onResize);
    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const caption = screen === 1 ? 1 : 0;
  return <div className="w-full max-w-[750px] mx-auto" data-id="element-9090" data-mp="38bjeftx4r9no">
      <div ref={containerRef} className="relative" data-id="element-9091" data-mp="wohbmlkz5uu2">
        <ChatGPTFrame step={screen} typed={typed} installHover={installHover} data-id="element-9092" data-mp="wp8y96wlgi87" />
        {/* Animated cursor */}
        <div className="absolute z-20 pointer-events-none transition-[left,top] duration-[850ms] ease-[cubic-bezier(0.33,0,0.2,1)]" style={{
        left: `${pointer.x}%`,
        top: `${pointer.y}%`
      }} data-id="element-9093" data-mp="slklm9t3uwg3">
          <svg width="23" height="23" viewBox="0 0 24 24" className={['block transition-transform duration-150', pressed ? 'scale-90' : 'scale-100'].join(' ')} style={{
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))'
        }} data-id="element-9094" data-mp="1gcyd6djfmo00">
            <path d="M5.5 3.2 L5.5 17.6 L9.2 14.1 L11.7 19.7 L14 18.7 L11.5 13.2 L16.6 13.2 Z" fill="#fff" stroke="#1A1A1A" strokeWidth="1.1" strokeLinejoin="round" data-id="element-9095" data-mp="1pclez0g2pvtl" />
          </svg>
        </div>
        {/* Caption overlaid on the frame — bottom centre, dark pill */}
        <div className="absolute inset-x-0 bottom-4 flex justify-center px-4 pointer-events-none z-30" data-id="element-9096" data-mp="3m66im4vib58s">
          <AnimatePresence mode="wait" data-id="element-9097" data-mp="1q3bj4id05673">
            <motion.span key={caption} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.3
          }} className="inline-flex items-center gap-2.5 bg-ink text-white rounded-full pl-2 pr-4 py-1.5 text-[13px] md:text-[14px] font-medium shadow-[0_6px_20px_rgba(0,0,0,0.25)]" data-id="element-9098" data-mp="1q4cxavpbe8t1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[12px] font-bold flex-none" data-id="element-9099" data-mp="2e07gkb5zc3y7">{caption + 1}</span>
              {CAPTIONS[caption]}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>;
}

// ── "More than answering questions" — one ad campaign carried from analyze
// → decide → coordinate → execute. Tabs advance as the section scrolls, and
// each answer is rendered the way ChatGPT actually shows it: plain text,
// bullets and a simple table, led by the "working" indicator.
const CHAIN = [{
  verb: 'Analyze',
  icon: BarChart3,
  prompt: 'hey! can you analyze our Q3 ad campaign performance?',
  kind: 'analyze',
  leadIn: "I'll pull the last 7 days across your four ad sets, then flag what's dragging the account down.",
  statuses: ['Analyzing Meta Ads Performance', 'Reviewing the last 7 days']
}, {
  verb: 'Decide',
  icon: Lightbulb,
  prompt: 'which ad sets should we cut, and where should the budget go?',
  kind: 'decide',
  leadIn: 'Let me weigh each ad set against your ROAS and CPA targets and give you a clear call.',
  statuses: ['Comparing to Your Targets', 'Weighing the options']
}, {
  verb: 'Coordinate',
  icon: Users,
  prompt: 'can you let the team know in Slack?',
  kind: 'coordinate',
  leadIn: "I'll drop a clear summary in #growth so nobody's caught off guard.",
  statuses: ['Posting to #growth', 'Notifying the team']
}, {
  verb: 'Execute',
  icon: Zap,
  prompt: 'go ahead — pause 2 & 3 and scale up 1 & 4',
  kind: 'execute',
  leadIn: "On it — I'll make the changes directly in Meta Ads Manager.",
  statuses: ['Updating Meta Ads Manager', 'Applying changes']
}];
function BeatContent({
  b,
  working
}: {
  b: (typeof CHAIN)[number];
  working: boolean;
}) {
  const rows = [{
    name: 'Ad set 1 · Broad',
    roas: '4.2×',
    cpa: '$18',
    good: true
  }, {
    name: 'Ad set 2 · Lookalike',
    roas: '1.1×',
    cpa: '$72',
    good: false
  }, {
    name: 'Ad set 3 · Interests',
    roas: '0.9×',
    cpa: '$88',
    good: false
  }, {
    name: 'Ad set 4 · Retargeting',
    roas: '3.8×',
    cpa: '$21',
    good: true
  }];
  return <div className="flex flex-col gap-3.5 text-[15px] leading-[1.6] text-ink" data-id="element-9100" data-mp="1h9f064if6tpk">
      {/* User prompt — right-aligned grey bubble */}
      <div className="self-end max-w-[90%] bg-[#F4F4F4] rounded-[24px] px-5 py-3 text-[#0D0D0D]" data-id="element-9101" data-mp="24xyaepl15gka">
        <span className="inline-flex items-center gap-1.5 mr-2 align-[-5px]" data-id="element-9102" data-mp="29qrf5dgi0owd">
          <img src={GIGA_MARK} alt="" className="w-[22px] h-[22px] rounded-[6px]" data-id="element-9103" data-mp="2j3zxanod4hqd" />
          <strong className="font-semibold" data-id="element-9104" data-mp="2f2wgnyyhqho3">Giga</strong>
        </span>
        {b.prompt}
      </div>
      <span className="self-end -mt-1.5 pr-0.5 text-ink-faint" data-id="element-9105" data-mp="2subaz1xum5ag"><Copy className="w-[18px] h-[18px]" data-id="element-9106" data-mp="2stta1s08r2w7" /></span>
      {/* Assistant answer — left-aligned */}
      <p data-id="element-9107" data-mp="2nvrqz72tf3kq">{b.leadIn}</p>
      {working ? <GigaWorking statuses={b.statuses} bare data-id="element-9108" data-mp="2nxko9vrjgbg2" /> : <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.25
    }} className="flex flex-col gap-3.5" data-id="element-9109" data-mp="206ozl7idpwd1">
            {b.kind === 'analyze' && <>
              <div className="overflow-hidden rounded-md border border-sand-200 text-[13.5px]" data-id="element-9110" data-mp="2a4mkpm4xzd1f">
                <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] bg-cream border-b border-sand-200 text-[11px] font-semibold text-ink-muted" data-id="element-9111" data-mp="253yx7afx9eby">
                  <span className="px-2.5 py-1.5" data-id="element-9112" data-mp="3lktbpwb3nukp">Ad set</span><span className="px-2.5 py-1.5 text-right" data-id="element-9113" data-mp="3giub2u6qvbrg">ROAS</span><span className="px-2.5 py-1.5 text-right" data-id="element-9114" data-mp="3gm9u283jh4y5">CPA</span>
                </div>
                {rows.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-sand-100 last:border-0" data-id="element-9115" data-mp="3sm6nv6vk5sll">
                    <span className="px-2.5 py-1.5 text-ink truncate" data-id="element-9116" data-mp="1fm2a2o9vcvy">{r.name}</span>
                    <span className={['px-2.5 py-1.5 text-right', r.good ? 'text-ink' : 'text-red-600 font-semibold'].join(' ')} data-id="element-9117" data-mp="6eqaapzgn4kp">{r.roas}</span>
                    <span className="px-2.5 py-1.5 text-right text-ink-muted" data-id="element-9118" data-mp="34y6yobdwaa94">{r.cpa}</span>
                  </div>)}
              </div>
              <p data-id="element-9119" data-mp="39tchh1c038sz">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
            </>}
            {b.kind === 'decide' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-9120" data-mp="3jls1s5ars9h1">
                <li data-id="element-9121" data-mp="3jnbfpp8ye8fn">Pause <strong data-id="element-9122" data-mp="2m64i10196tyv">ad set 2</strong> — 1.1× ROAS, $72 CPA</li>
                <li data-id="element-9123" data-mp="2m5uox3kww6oy">Pause <strong data-id="element-9124" data-mp="2m40ckdnphmwh">ad set 3</strong> — 0.9× ROAS, $88 CPA</li>
                <li data-id="element-9125" data-mp="2r3wgkcwelec1">Reallocate that budget to ad sets 1 &amp; 4, your top performers</li>
              </ul>}
            {b.kind === 'coordinate' && <blockquote className="border-l-2 border-sand-300 pl-3 text-ink-muted" data-id="element-9126" data-mp="2jjc4kn66udxg">Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4. Full breakdown in the thread.</blockquote>}
            {b.kind === 'execute' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-9127" data-mp="24p7a05s0t3md">
                <li data-id="element-9128" data-mp="20mu49hd9x5pl">Paused ad set 2</li>
                <li data-id="element-9129" data-mp="20nvs6mfe1moq">Paused ad set 3</li>
                <li data-id="element-9130" data-mp="20ox3ms0bgb6z">Increased ad set 1 budget by 40%</li>
                <li data-id="element-9131" data-mp="1vv2ntznu0gd6">Increased ad set 4 budget by 40%</li>
              </ul>}
          </motion.div>}
    </div>;
}
export function MoreThanAnswers({
  embedded = false
}: {
  embedded?: boolean;
} = {}) {
  const [active, setActive] = useState(0);
  const [working, setWorking] = useState(true);
  const [nonce, setNonce] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  // Jump to a tab from a click, bumping `nonce` so the auto-advance + working
  // timers restart in full — even when re-clicking the tab that's showing.
  const goTo = (i: number) => {
    setActive(i);
    setNonce(n => n + 1);
  };
  // Start from the first tab whenever the section scrolls into view.
  useEffect(() => {
    if (inView) setActive(0);
  }, [inView]);
  // Auto-advance every 3s while in view; changing tab or clicking restarts it.
  useEffect(() => {
    if (!inView) return;
    const id = setTimeout(() => setActive(a => (a + 1) % CHAIN.length), 3000);
    return () => clearTimeout(id);
  }, [active, inView, nonce]);
  // The "working" indicator plays briefly on each tab, then settles into the
  // finished answer.
  useEffect(() => {
    setWorking(true);
    const id = setTimeout(() => setWorking(false), 1600);
    return () => clearTimeout(id);
  }, [active, nonce]);
  const b = CHAIN[active];
  return <div ref={ref} className={['flex flex-col items-center', embedded ? 'gap-0' : 'gap-6 md:gap-8'].join(' ')} data-id="element-9132" data-mp="2365vg4fpmowo">
      {!embedded && <div className="flex flex-col items-center text-center gap-4" data-id="element-9133" data-mp="1kh3l4zrutnmn">
          <h2 className="font-title font-extrabold text-4xl md:text-[54px] leading-[1.02] tracking-tight max-w-[18ch]" data-id="element-9134" data-mp="1fiscgsdapj60">More than just answering questions</h2>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted max-w-[52ch]" data-id="element-9135" data-mp="1tk4oispc50mm">Most AI stops at an answer. Giga takes action and gets the work done.</p>
          <div className="inline-flex items-center gap-1 bg-cream border border-sand-200 rounded-full p-1 mt-1" data-id="element-9136" data-mp="23fxz46jhuk1r">
            {CHAIN.map(({
          verb,
          icon: Icon
        }, i) => <button key={verb} type="button" onClick={() => goTo(i)} className={['inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full text-[13px] md:text-[15px] font-semibold transition-colors duration-300', i === active ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'].join(' ')} data-id="element-9137" data-mp="2c8t5j3kp6lt4" data-mpi={i}><Icon className="w-4 h-4" data-id="element-9138" data-mp="2h85txsr7w0xt" />{verb}</button>)}
          </div>
        </div>}
      <div className={['w-full bg-white border border-sand-200 rounded-2xl p-5 md:p-7 shadow-[0_6px_28px_rgba(43,33,26,0.06)]', embedded ? 'max-w-none min-h-[430px]' : 'max-w-[640px] min-h-[400px]'].join(' ')} data-id="element-9139" data-mp="2h61hunj5zvmg">
        <AnimatePresence mode="wait" data-id="element-9140" data-mp="1o9dxbsvpgbh2">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25
        }} data-id="element-9141" data-mp="1oaflmz1qeugq">
            <BeatContent b={b} working={working} data-id="element-9142" data-mp="1t8h2reflkjpk" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}

// ── "Put it down anywhere" — Giga is its own system, reachable from any
// provider, so nothing is locked in.
const HANDOFF_TARGETS = ['Slack', 'Claude Code', 'your other tools'];
const PROVIDERS = [{
  name: 'ChatGPT',
  logo: OPENAI_ICON
}, {
  name: 'Slack',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
}, {
  name: 'Claude',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/anthropic.svg'
}];
const SYSTEM_BOXES = [{
  icon: Users,
  name: 'Team',
  desc: 'Users, groups and spaces.'
}, {
  icon: Folder,
  name: 'Projects',
  desc: 'Ongoing work, kept together.'
}, {
  icon: Plug,
  name: 'Connectors',
  desc: 'All your tools, connected once.'
}, {
  icon: Sparkles,
  name: 'Skills',
  desc: 'Reusable know-how, applied on demand.'
}, {
  icon: FileText,
  name: 'Files',
  desc: 'Docs, decks and exports, understood.'
}, {
  icon: Database,
  name: 'Memories',
  desc: 'Shared, inspectable company knowledge.'
}];

// The tool name at the end of the headline, rotating on a loop.
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % HANDOFF_TARGETS.length), 2200);
    return () => clearInterval(id);
  }, []);
  return <span className="text-brand whitespace-nowrap" data-id="element-9143" data-mp="3m389bbrlsyu1">
      <AnimatePresence mode="wait" data-id="element-9144" data-mp="3q77lji5upbhl">
        <motion.span key={i} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} className="inline-block" data-id="element-9145" data-mp="3q64wlfs39j4b">{HANDOFF_TARGETS[i]}</motion.span>
      </AnimatePresence>
    </span>;
}
export function GigaForChatGPT2() {
  // Real integrations from the Giga catalog (same source as the /integrations
  // page). Seed from the prerender prime cache when present, else fetch on
  // mount. Purely display filler for the right columns — no filtering wired up.
  const primed = getCachedIntegrations();
  const [integrations, setIntegrations] = useState<IntegrationListItem[]>(primed ?? []);
  useEffect(() => {
    if (primed && primed.length) return;
    let cancelled = false;
    fetchIntegrations().then(rows => {
      if (!cancelled) setIntegrations(rows);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // No popularity field on the catalog, so curate: known tools to the top (in
  // POPULAR_SLUGS order), the rest keep the API's alphabetical order behind them.
  const ranked = [...integrations].sort((a, b) => (POPULAR_RANK.get(a.slug) ?? Infinity) - (POPULAR_RANK.get(b.slug) ?? Infinity));
  const shown = ranked.slice(0, 60);
  return <div className="w-full bg-cream text-ink font-body" data-id="element-9146" data-mp="2lutsw7vwl15o">
      <div className="max-w-[1120px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28 flex flex-col gap-10 md:gap-14" data-id="element-9147" data-mp="2qvgdthtmngew">

        {/* ── Hero ──────────────────────────────────────────────── */}
        <section className="flex flex-col items-center text-center gap-5" data-id="element-9148" data-mp="30ndrcv83ykgs">
          <div className="flex items-center gap-2.5" data-id="element-9149" data-mp="35lnqti0v8787">
            <img src={OPENAI_ICON} alt="ChatGPT" className="w-5 h-5" data-id="element-9150" data-mp="35n8h2vg5d409" />
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-faint" data-id="element-9151" data-mp="39o247mlrmes3">Giga inside ChatGPT</span>
          </div>
          <h1 className="font-title font-extrabold text-4xl md:text-[56px] leading-[1.03] tracking-tight max-w-[18ch]" data-id="element-9152" data-mp="3ejhhodfv28nz">
            The marketing operating system inside ChatGPT.
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty max-w-[58ch]" data-id="element-9153" data-mp="2wvfad57y3624">
            Every marketing tool your team touches — connected to the chat you already have open.
          </p>
        </section>

        {/* ── Connect all your tools ────────────────────────────── */}
        <section className="rounded-[28px] border border-sand-200 bg-white p-6 md:p-12 flex flex-col gap-10 md:gap-12" data-id="element-9154" data-mp="2i22dq5auk5kh">
          <div className="flex flex-col gap-3 max-w-[60ch]" data-id="element-9155" data-mp="2ry5i7ht68gal">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9156" data-mp="2rwc6vp7m57w9">Integrations</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9157" data-mp="2n1orcful7ntt">
              Connect <em className="italic text-brand underline decoration-2 underline-offset-[6px]" data-id="element-9158" data-mp="29d5q5ethqjy">all</em> your tools, not just MCP.
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9159" data-mp="1k56j65cr2jyo">
              ChatGPT connects to the handful of tools that speak MCP. Giga connects to{' '}
              <strong className="text-brand font-semibold" data-id="element-9160" data-mp="1b739c6b4nbmp">literally everything else</strong>.
            </p>
          </div>

          {/* A · Segmented coverage meters */}
          <div className="rounded-2xl border border-sand-200 bg-cream p-6 md:p-8 flex flex-col gap-7" data-id="element-9161" data-mp="23u77brbsypoz">
            {METERS.map((group, gi) => <div key={group.metric} className={['flex flex-col gap-4', gi > 0 ? 'pt-7 border-t border-sand-200' : ''].join(' ')} data-id="element-9162" data-mp="27v0w0mx2k8ix" data-mpi={gi}>
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9163" data-mp="1u2tcvxypkg2p">{group.metric}</span>
                <div className="flex flex-col gap-3.5" data-id="element-9164" data-mp="2mtl2gcl9q6h2">
                  {group.rows.map(r => <MeterRow key={r.who} who={r.who} score={r.score} brand={r.brand} data-id="element-9165" data-mp="2mri6lbxfpj4f" />)}
                </div>
              </div>)}
          </div>

          {/* B · Ontology — categories on the left, real integrations right */}
          <div className="flex flex-col gap-4" data-id="element-9166" data-mp="2whgogm08gsna">
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9167" data-mp="2whqgsg8r3bw5">Marketing tools by category</span>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-9168" data-mp="2rjxaln7g6hvv">

              {/* Left · category filters (fillers — not wired to the grid) */}
              <div className="lg:col-span-1 flex flex-col gap-2 lg:max-h-[600px] lg:overflow-y-auto lg:pr-2" data-id="element-9169" data-mp="2ii7lio11fv19">
                <span className="inline-flex items-center gap-2 rounded-lg border border-brand bg-white py-2 px-3 text-[13px] font-semibold text-brand" data-id="element-9170" data-mp="2dp4q0acpabif">
                  <Filter className="w-4 h-4" data-id="element-9171" data-mp="23t1nh3dxsauy" />All Categories
                </span>
                {MARKETING_CATEGORIES.map(({
                name,
                icon: Icon
              }) => <span key={name} className="inline-flex items-center gap-2 rounded-lg border border-sand-200 bg-white py-2 px-3 text-[13px] font-medium text-ink hover:border-sand-300 transition-colors cursor-default" data-id="element-9172" data-mp="1urtp6lbf7a0v">
                    <Icon className="w-4 h-4 text-ink-faint flex-none" data-id="element-9173" data-mp="eauyd606ojb2" />{name}
                  </span>)}
              </div>

              {/* Right · real integrations from the catalog (3 columns) */}
              <div className="lg:col-span-3" data-id="element-9174" data-mp="oa3pzs7ktlu9">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:max-h-[600px] lg:overflow-y-auto lg:pr-1" data-id="element-9175" data-mp="o8sltra7yc2m">
                  {shown.length === 0 ? Array.from({
                  length: 12
                }).map((_, i) => <div key={i} className="h-[58px] rounded-xl bg-cream border border-sand-200 animate-pulse" data-id="element-9176" data-mp="3ro7qse3qpvgw" data-mpi={i} />) : shown.map(item => <IntegrationTile key={item.slug} item={item} available={availableInChatGPT(item)} data-id="element-9177" data-mp="5aptp3mdjz0l" />)}
                </div>
              </div>

            </div>
          </div>

        </section>

        {/* ── Never hit a dead end — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-9178" data-mp="38xyjwqx0dfiv">
          <div className="flex flex-col items-center text-center gap-3 max-w-[58ch] mx-auto" data-id="element-9179" data-mp="22d9xub94ng15">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9180" data-mp="1xefgldqt5zj5">Add anything</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9181" data-mp="2beq9emboho6y">Never hit a dead end.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9182" data-mp="26gew21m6cvjz">
              No integration yet? If a tool isn't in Giga's directory, it finds it on the web and
              builds a connection on the spot.
            </p>
          </div>

          {/* Ask → Giga runs the checks & builds it → connect link */}
          <div className="flex flex-col items-center w-full" data-id="element-9183" data-mp="2q3494odsswja">
            {/* The ask */}
            <div className="w-full max-w-[700px]" data-id="element-9184" data-mp="34118ttnjtj26">
              <ChatBox send data-id="element-9185" data-mp="33zq5tw1xgfc4">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-9186" data-mp="2z2zqjaxy5nrr">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9187" data-mp="3io9ph9zmmglj" />@Giga
                </span>
                can we connect Larkspur?
              </ChatBox>
            </div>

            <FlowLink data-id="element-9188" data-mp="38r52x91vcj47" />

            {/* Giga runs the checks and builds it */}
            <div className="w-full max-w-[560px] relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-9189" data-mp="3dp6llsvdugf5">
              <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9190" data-mp="2zvyr9tfx36tr">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9191" data-mp="2q043yl0r0nkn" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9192" data-mp="2pxrelmsnvaxk">Giga builds it</span>
              </div>
              {DEADEND_CHECKS.map(({
              icon: Icon,
              label,
              status,
              tone
            }) => <div key={label} className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-2.5" data-id="element-9193" data-mp="2kzhhv7rag29u">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9194" data-mp="180klsi9afxsm"><Icon className="w-4 h-4" data-id="element-9195" data-mp="1cznss88twe38" /></span>
                  <span className="text-[13px] text-ink flex-1 min-w-0" data-id="element-9196" data-mp="1hvw0x1olz70w">{label}</span>
                  {tone === 'green' ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 flex-none" data-id="element-9197" data-mp="1mws75lwfn59h"><Check className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-9198" data-mp="1qwuas0lqa8re" />{status}</span> : <span className="text-[11px] text-ink-faint flex-none" data-id="element-9199" data-mp="1vw6tqa9havop">{status}</span>}
                </div>)}
            </div>

            <FlowLink data-id="element-9200" data-mp="20oq8vbntdyvm" />

            {/* Giga's reply with the connect link */}
            <div className="w-full max-w-[700px]" data-id="element-9201" data-mp="25mrsbxp5k07m">
              <ChatBox data-id="element-9202" data-mp="25okwn6cam8cg">
                <div className="flex items-start gap-2.5" data-id="element-9203" data-mp="2aluw7etec0e0">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none mt-0.5" data-id="element-9204" data-mp="2anfqr4fpnjbv" />
                  <div className="flex flex-col gap-2.5" data-id="element-9205" data-mp="2el3rwp7zy8c7">
                    <span data-id="element-9206" data-mp="30w4gtzzzo55">Done — I've built the <strong className="font-semibold" data-id="element-9207" data-mp="315wso8im7e0">Larkspur</strong> integration. Click to connect it:</span>
                    <a href="#" className="self-start inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white rounded-full px-4 py-2 text-[13px] font-bold transition-colors" data-id="element-9208" data-mp="3v5qcn4slbnh9">
                      Connect Larkspur <ArrowRight className="w-3.5 h-3.5" data-id="element-9209" data-mp="3l7soehaqdqon" />
                    </a>
                  </div>
                </div>
              </ChatBox>
            </div>
          </div>

        </section>

        {/* ── Secure vault — its own panel ───────────────────────── */}
        <section className="rounded-[28px] border border-sand-200 bg-white p-6 md:p-12 flex flex-col gap-8 md:gap-10" data-id="element-9210" data-mp="cw697phs69mm">
          <div className="flex flex-col items-center text-center gap-3 max-w-[58ch] mx-auto" data-id="element-9211" data-mp="7x3d4ujs3dqs">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9212" data-mp="15gega650zz9y">Secure vault</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9213" data-mp="2uvffwn68mmxl">Keep private data out of the conversation.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9214" data-mp="2ywiw5as76kzc">
              API keys, OAuth tokens, and database credentials stay securely in Giga's encrypted
              vault. ChatGPT gets the access it needs—without ever seeing your secrets.
            </p>
          </div>

          {/* Prompt → vault → answer, one centred column */}
          <div className="flex flex-col items-center w-full" data-id="element-9215" data-mp="2g6t2ws43279j">
            <div className="w-full max-w-[700px]" data-id="element-9216" data-mp="2g46ms434mjfp">
              <ChatBox send data-id="element-9217" data-mp="228367dl6e9lp">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-9218" data-mp="228uqvtd8imx7">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9219" data-mp="fezuvd8j61p" />@Giga
                </span>
                what were our metrics last week?
              </ChatBox>
            </div>

            <FlowLink data-id="element-9220" data-mp="fi6odw59wrm5" />

            <div className="w-full max-w-[560px] flex flex-col gap-2.5" data-id="element-9221" data-mp="fhn7mird099p">
              <div className="relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-9222" data-mp="ajlltq2jtvuj">
                <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9223" data-mp="ocb8zxttd6t8">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9224" data-mp="ji721ekjsys5" />
                  <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9225" data-mp="y7d0v1fh4mzi">Giga Vault</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-9226" data-mp="179mazq6tyecd">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9227" data-mp="137r4m7a1larq"><Database className="w-4 h-4" data-id="element-9228" data-mp="391qx2l1nehf6" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-9229" data-mp="34znhbk7y4qo3">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-9230" data-mp="3516zxhh2rdt1">Database credentials</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-9231" data-mp="36inlkpq091gn">postgres://••••••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-9232" data-mp="3fgzeiyecgbc9"><ShieldCheck className="w-3.5 h-3.5" data-id="element-9233" data-mp="3kgki5m4gl7vb" />Secured by Giga</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-9234" data-mp="2nqurst343c7v">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9235" data-mp="2rs6kj7ha8dka"><KeyRound className="w-4 h-4" data-id="element-9236" data-mp="2won6pw4x8huq" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-9237" data-mp="31ihxfjiy1x3b">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-9238" data-mp="2422fcvog45r6">API token</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-9239" data-mp="2915i29gydzvz">Bearer sk_live_••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-9240" data-mp="21gbj750zp8fx"><ShieldCheck className="w-3.5 h-3.5" data-id="element-9241" data-mp="1wjusc32eofz5" />Secured by Giga</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-9242" data-mp="1rhncv3idahs2"><Check className="w-3.5 h-3.5 text-emerald-600 flex-none" strokeWidth={2.5} data-id="element-9243" data-mp="1nh3fpzxsne5x" /><span data-id="element-9244" data-mp="1ij1v3akpz6sc"><strong className="text-ink font-medium" data-id="element-9245" data-mp="1ij1yzlnubqxm">MCP &amp; OAuth tools</strong> skip the vault — already secure.</span></span>
            </div>

            <FlowLink data-id="element-9246" data-mp="1dpz336vlc5e9" />

            <div className="w-full max-w-[700px]" data-id="element-9247" data-mp="18spcvp0dnppd">
              <ChatBox data-id="element-9248" data-mp="18tifcdsazt1p">
                <span className="inline-flex items-center gap-2" data-id="element-9249" data-mp="18qmccb1lme6e">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none" data-id="element-9250" data-mp="13vgt5jzkzdm0" />
                  <span data-id="element-9251" data-mp="zrr8v6pxvi6s">Last week: <strong className="font-semibold" data-id="element-9252" data-mp="zuddoylg3jlb">1,240 signups · $48k revenue · 3.4× ROAS</strong>.</span>
                </span>
              </ChatBox>
            </div>
          </div>
        </section>

        {/* ── More than answering questions — analyze→execute, tabbed on scroll ── */}
        <section className="py-2 md:py-8" data-id="element-9253" data-mp="p8i6s3fkz9e5">
          <MoreThanAnswers data-id="element-9254" data-mp="b7xl8zia6zgy" />
        </section>

        {/* ── Embedded expertise ────────────────────────────────── */}
        <section className="rounded-[28px] border border-sand-200 bg-white p-6 md:p-12 flex flex-col gap-8 md:gap-10" data-id="element-9255" data-mp="g7jy37u76dp8">
          <div className="flex flex-col items-center text-center gap-3 max-w-[56ch] mx-auto" data-id="element-9256" data-mp="13wlcrctmil2y">
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9257" data-mp="18tv0mo1d6ooo">Embedded expertise.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9258" data-mp="u4fl647aa5oa">
              Giga comes pre-packed with the full landscape of approaches, strategies, skills, and
              tools — a whole library you can put to work.
            </p>
          </div>

          {/* Two ChatGPT windows: plain question vs @Giga */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start" data-id="element-9259" data-mp="3cb9ml7nt5c9l">
            {/* ── ChatGPT ── */}
            <div className="flex flex-col gap-3" data-id="element-9260" data-mp="37cgkebdz17oq">
              <div className="flex items-center gap-2" data-id="element-9261" data-mp="3l44w4dkb5u56">
                <img src={OPENAI_ICON} alt="" className="w-4 h-4" data-id="element-9262" data-mp="3l3lc8rb3kvoi" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9263" data-mp="3h2jdebl3eln2">ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-9264" data-mp="2tdgwj5ya8wtm">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-9265" data-mp="32e587u07el3c">What is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9266" data-mp="2xeixbr7ulgxp"><ArrowUp className="w-3.5 h-3.5" data-id="element-9267" data-mp="hmmoo78g6p5a" /></span>
              </div>
              <div className="bg-white border border-sand-200 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-9268" data-mp="cp2z4b6aupy1">
                <p data-id="element-9269" data-mp="wrz8tfa6xuf1"><strong className="font-semibold" data-id="element-9270" data-mp="ws8xmzji1vj5">User-generated content (UGC) marketing</strong> is a strategy where brands use content made by their customers or unpaid creators — reviews, photos, videos, and social posts — instead of producing it themselves.</p>
                <p data-id="element-9271" data-mp="1aim6r584nga2">It's generally seen as more authentic and trustworthy than brand-made content, and it's widely used across e-commerce, social media, and paid advertising.</p>
                <p className="text-ink-muted" data-id="element-9272" data-mp="1kg9ydmafs7o0">Would you like some examples or best practices to get started?</p>
              </div>
            </div>

            {/* ── Giga ── */}
            <div className="flex flex-col gap-3" data-id="element-9273" data-mp="1pd8qjzaji4jk">
              <div className="flex items-center gap-2" data-id="element-9274" data-mp="1th83k7wm2l86">
                <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px]" data-id="element-9275" data-mp="1telz4h50olu6" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-brand-dark" data-id="element-9276" data-mp="3r8j58jd73rmu">Giga in ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-9277" data-mp="3noit44yxu8y3">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-9278" data-mp="3ivzc0y11kvoj"><span className="font-semibold text-brand-dark" data-id="element-9279" data-mp="3dy7m29jyjjmz">@Giga</span> what is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9280" data-mp="39y6z20ism04b"><ArrowUp className="w-3.5 h-3.5" data-id="element-9281" data-mp="agz6jwcpcvvf" /></span>
              </div>
              <div className="bg-white border border-brand/30 ring-1 ring-brand/10 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-9282" data-mp="5intlcr4258z">
                <GigaWorking data-id="element-9283" data-mp="1jotq6k8kyoe" />
                <p data-id="element-9284" data-mp="3so5bstxjchea">UGC is content made by your <strong className="font-semibold" data-id="element-9285" data-mp="3sn3qzxqpwgjd">customers and creators</strong> — not your brand. It works best for DTC, apps, and social-first teams.</p>
                <p data-id="element-9286" data-mp="2h8q877atmra7">Here's everything you'd need to run it:</p>
                <p data-id="element-9287" data-mp="2h7f3n59jxfi1">📖 <a href="#" className="text-brand-dark font-semibold underline underline-offset-2 hover:text-brand" data-id="element-9288" data-mp="2h7f4f7hdljj3">The Giga Guide to UGC Marketing</a> — the canonical playbook (12 min read).</p>
                <p data-id="element-9289" data-mp="1cs8gm3qfllqz"><strong className="font-semibold" data-id="element-9290" data-mp="1cs8ik99zrvtm">42 tools</strong> across 6 subcategories — creator sourcing, editing &amp; captions, rights &amp; contracts, whitelisting, hooks &amp; scripts, and analytics. Want me to start with one?</p>
                <div className="flex flex-col gap-1.5" data-id="element-9291" data-mp="20iuc6vj91dk3">
                  <p data-id="element-9292" data-mp="20ib6cd6vifmf"><strong className="font-semibold" data-id="element-9293" data-mp="20ib4e7nbc5js">Skills you can install:</strong></p>
                  <ul className="flex flex-col gap-1" data-id="element-9294" data-mp="1wi0oa25t3yr7">
                    {UGC_SKILLS.map(sk => <li key={sk.name} className="flex gap-2" data-id="element-9295" data-mp="1rhd1xtsiw63b"><span className="text-brand-dark leading-6" data-id="element-9296" data-mp="1rkiob19rotvc">•</span><span data-id="element-9297" data-mp="1mk4f3fwfjdvq"><strong className="font-semibold" data-id="element-9298" data-mp="1mi9uy3szfpsp">{sk.name}</strong> — {sk.skill}</span></li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Put it down anywhere — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-9299" data-mp="10r89737gxhx">
          <div className="flex flex-col items-center text-center gap-3 max-w-[64ch] mx-auto" data-id="element-9300" data-mp="5zc12lg4oewc">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9301" data-mp="ayf6w83xmp5d">Portable by design</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[44px] leading-[1.08] tracking-tight" data-id="element-9302" data-mp="fpngp596ccj2">Put it down in ChatGPT, pick it up in <RotatingWord data-id="element-9303" data-mp="jpxliel8cwwc" /></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9304" data-mp="jt3alttuzys2">
              Giga is its own system — it lives outside any single provider. Your credentials, skills,
              and files come with you, so you can work from anywhere and you're never locked in.
            </p>
          </div>

          {/* Providers → one shared Giga system */}
          <div className="flex flex-col items-stretch max-w-[760px] mx-auto w-full" data-id="element-9305" data-mp="2vhultpo1df2p">
            <div className="grid grid-cols-3 gap-3 md:gap-6" data-id="element-9306" data-mp="30g5sxsmy5bh8">
              {PROVIDERS.map(p => <div key={p.name} className="flex flex-col items-center min-w-0" data-id="element-9307" data-mp="2sfq13db5b2u7">
                  <div className="w-full bg-white border border-sand-200 rounded-2xl shadow-sm p-3 md:p-4 flex items-center justify-center gap-2 md:gap-2.5" data-id="element-9308" data-mp="2ng3mazfo5eja">
                    <img src={p.logo} alt="" className="w-5 h-5 md:w-6 md:h-6 flex-none" data-id="element-9309" data-mp="32arrech5qayd" />
                    <span className="font-title font-bold text-[13px] md:text-[16px] truncate" data-id="element-9310" data-mp="2xcggrzj1fwf3">{p.name}</span>
                  </div>
                  <div className="w-0 h-8 md:h-10 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9311" data-mp="36eppqkynrhqd" />
                </div>)}
            </div>

            <div className="relative border-2 border-dashed border-[#D6CCC0] rounded-[28px] bg-sand-50 pt-11 px-4 md:px-8 pb-6 md:pb-8" data-id="element-9312" data-mp="3l2asuevj9gya">
              <div className="absolute -top-3.5 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9313" data-mp="3g6c7z19eyh2m">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9314" data-mp="3q2ys1ntx143l" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9315" data-mp="3q0m1wne07nfg">Giga · your company brain</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-id="element-9316" data-mp="hjd9zsl45hwk">
                {SYSTEM_BOXES.map(({
                icon: Icon,
                name,
                desc
              }) => <div key={name} className="bg-white border border-sand-200 rounded-2xl p-4 flex flex-col gap-2" data-id="element-9317" data-mp="8iousujzhbi3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10 text-brand-dark" data-id="element-9318" data-mp="3pzg7j5iihq76"><Icon className="w-5 h-5" data-id="element-9319" data-mp="3usj2ni2xt7ph" /></span>
                    <span className="font-title font-bold text-[15px]" data-id="element-9320" data-mp="3gztbz0cgrem1">{name}</span>
                    <span className="text-[12.5px] leading-[1.45] text-ink-muted" data-id="element-9321" data-mp="3l14w4qckj7mu">{desc}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* ── Built for teams ───────────────────────────────────── */}
        <section className="rounded-[28px] border border-sand-200 bg-white p-6 md:p-12 flex flex-col gap-8 md:gap-10" data-id="element-9322" data-mp="3bzxbvc6w07br">
          <div className="flex flex-col items-center text-center gap-3 max-w-[60ch] mx-auto" data-id="element-9323" data-mp="ial4milb6w3q">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9324" data-mp="e8zyrh76zi31">Connections</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9325" data-mp="9du2ppk2sn18">Built for teams.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9326" data-mp="3vnwp09esvwlx">
              Connections, files, skills — share any of it exactly where it belongs: a whole group,
              a single project space, or one person. No re-entering keys, no shared passwords.
            </p>
          </div>

          {/* Sharing visual — a credential / file / skill, shared where it belongs */}
          <TeamSharingVisual data-id="element-9327" data-mp="1kvlqzx3xn1m2" />
        </section>

        {/* ── Get started — auto-playing install walkthrough ────── */}
        <section className="flex flex-col items-center gap-8 md:gap-10 py-4 md:py-10" data-id="element-9328" data-mp="1fxk5z6my4s7y">
          <div className="flex flex-col items-center text-center gap-3 max-w-[56ch]" data-id="element-9329" data-mp="1neqj3y3pi1ju">
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9330" data-mp="1se30w4fq0ifk">Get started in 30 seconds</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9331" data-mp="1x6cze8uwiqtg">Installing Giga is as simple as going to plugins.</p>
          </div>
          <GetStartedFlow data-id="element-9332" data-mp="14hpa82717ham" />
          <a href="https://chatgpt.com/plugins?q=giga" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded-full px-7 py-3.5 text-[15px] font-bold transition-colors shadow-sm shadow-brand/20" data-id="element-9333" data-mp="14igtcdjfzmk0">
            Get started <ArrowRight className="w-4 h-4" data-id="element-9334" data-mp="1ihqdfoarr0t9" />
          </a>
        </section>

      </div>
    </div>;
}